import { useEffect, useMemo, useState } from "react";
import {
  MDBBadge,
  MDBBtn,
  MDBCol,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBRow,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../../constants";
import PackagesEditor, {
  chainOf,
  levelsOf,
  withQuantities,
} from "./packagesEditor";
import { preferredIdOf, supplierIdsOf, supplierLabel } from "./common";

// generics usually received with a lot number and expiry
const LOT_CATEGORIES = [
  "Reagent",
  "Test Kit",
  "Control",
  "Calibrator",
  "Stain",
  "Culture Media",
  "Collection Tube",
];
const tracksLotByDefault = (pgid) =>
  LOT_CATEGORIES.includes(GenericProducts.find(pgid)?.category);

// base unit dispensed: tests for reagents, else pieces
const baseOf = (pgid) =>
  GenericProducts.find(pgid)?.uom === "test" ? "test" : "pcs";

const blankVariant = (base = "pcs") => ({
  name: "",
  // breakdown, largest first; the last is the base unit
  breakdown: [{ bu: base, su: 1 }],
  reorderLevel: "",
  reorderUnit: "",
  isDefault: false,
});

/**
 * Breakdown of a saved variant for the form; older records (packaging / tests)
 * are converted, a variant without one starts with the base unit only.
 */
const formLevelsOf = (variant, product, base = "pcs") => {
  const levels = levelsOf(variant, product);
  return levels.length
    ? levels.map(({ bu, su }) => ({ bu: bu || "", su: su || 1 }))
    : [{ bu: base, su: 1 }];
};

// where a brand is kept (server Products.storage)
export const STORAGE_TYPES = {
  roomTemp: "Room temp",
  refrigerated: "Refrigerated (2–8°C)",
  frozen: "Frozen",
};

const blank = {
  pgid: "",
  name: "",
  manufacturer: "",
  suppliers: [],
  supplier: "",
  tracksLot: false,
  storage: "roomTemp",
  description: "",
  isDefault: false,
  variants: [{ ...blankVariant(), isDefault: true }],
};

// saved product -> form; older records without variants get one from their packaging/tests
const toForm = (product = {}) => ({
  ...blank,
  pgid: product.pgid || "",
  name: product.name || "",
  manufacturer: product.manufacturer || "",
  suppliers: supplierIdsOf(product),
  supplier: preferredIdOf(product),
  tracksLot:
    typeof product.tracksLot === "boolean"
      ? product.tracksLot
      : tracksLotByDefault(product.pgid),
  storage: product.storage || "roomTemp",
  description: product.description || "",
  isDefault: !!product.isDefault,
  variants: product.variants?.length
    ? product.variants.map((variant) => ({
        _id: variant._id,
        name: variant.name || "",
        breakdown: formLevelsOf(variant, product, baseOf(product.pgid)),
        reorderLevel: variant.reorderLevel ?? "",
        reorderUnit: variant.reorderUnit || "",
        isDefault: !!variant.isDefault,
      }))
    : [
        {
          ...blankVariant(baseOf(product.pgid)),
          name: product._id ? "Standard" : "",
          breakdown: formLevelsOf({}, product, baseOf(product.pgid)),
          isDefault: true,
        },
      ],
});

/**
 * Add / edit a branded product, tagged to one generic and to the suppliers
 * that carry it (one of them preferred, used when ordering).
 */
export default function ProductForm({
  show,
  toggle,
  selected,
  onSubmit,
  suppliers = [],
  isLoading,
}) {
  const [form, setForm] = useState(blank),
    [genericKey, setGenericKey] = useState(""),
    [supplierKey, setSupplierKey] = useState(""),
    // variants with their packaging editor open
    [openPackages, setOpenPackages] = useState({}),
    [error, setError] = useState("");
  const willCreate = !selected?._id;

  useEffect(() => {
    if (!show) return;
    setForm(toForm(selected || {}));
    setGenericKey("");
    setSupplierKey("");
    setOpenPackages({});
    setError("");
  }, [show, selected]);

  const generic = useMemo(() => GenericProducts.find(form.pgid), [form.pgid]);
  const needsTests = generic?.uom === "test";

  // active generics matching the search, grouped by section; the chosen one always stays
  const genericGroups = useMemo(() => {
    const key = genericKey.trim().toLowerCase();
    return GenericProducts.active
      .filter(
        ({ id, name = "", abbreviation = "", subname = "" }) =>
          !key ||
          id === Number(form.pgid) ||
          [name, abbreviation, subname].some((value) =>
            value.toLowerCase().includes(key),
          ),
      )
      .reduce((result, item) => {
        (result[item.section] = result[item.section] || []).push(item);
        return result;
      }, {});
  }, [genericKey, form.pgid]);

  const shownSuppliers = useMemo(() => {
    const key = supplierKey.trim().toLowerCase();
    return suppliers.filter(
      (supplier) =>
        !key ||
        form.suppliers.includes(supplier._id) ||
        supplierLabel(supplier).toLowerCase().includes(key),
    );
  }, [suppliers, supplierKey, form.suppliers]);

  const handleChange = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: value,
      // a new brand follows its generic: reagents, kits, tubes... track lots
      ...(key === "pgid" &&
        willCreate && { tracksLot: tracksLotByDefault(value) }),
    }));

  // the first tagged supplier becomes preferred; untagging the preferred moves it
  const toggleSupplier = (id) =>
    setForm((prev) => {
      const tagged = prev.suppliers.includes(id);
      const next = tagged
        ? prev.suppliers.filter((value) => value !== id)
        : [...prev.suppliers, id];
      const supplier =
        prev.supplier && next.includes(prev.supplier)
          ? prev.supplier
          : next[0] || "";
      return { ...prev, suppliers: next, supplier };
    });

  const setPreferred = (id) =>
    setForm((prev) => ({
      ...prev,
      supplier: id,
      suppliers: prev.suppliers.includes(id)
        ? prev.suppliers
        : [...prev.suppliers, id],
    }));

  const handleVariant = (index, key, value) =>
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, i) =>
        key === "isDefault"
          ? { ...variant, isDefault: i === index }
          : i === index
            ? { ...variant, [key]: value }
            : variant,
      ),
    }));

  // the default of the generic is one variant: its brand is the default brand
  // and it is the default variant of that brand. Clicking it again unsets it.
  const toggleDefault = (index) =>
    setForm((prev) => {
      const isCurrent = prev.isDefault && prev.variants[index]?.isDefault;
      return {
        ...prev,
        isDefault: !isCurrent,
        variants: prev.variants.map((variant, i) => ({
          ...variant,
          isDefault: isCurrent ? variant.isDefault : i === index,
        })),
      };
    });

  const addVariant = () =>
    setForm((prev) => ({
      ...prev,
      variants: [...prev.variants, blankVariant(baseOf(prev.pgid))],
    }));

  const removeVariant = (index) =>
    setForm((prev) => {
      const removedDefault = prev.variants[index]?.isDefault;
      const variants = prev.variants.filter((_, i) => i !== index);
      // keep one default variant per brand
      if (variants.length && !variants.some(({ isDefault }) => isDefault))
        variants[0] = { ...variants[0], isDefault: true };
      // the default of the generic is not moved to another variant silently
      return {
        ...prev,
        variants,
        isDefault: prev.isDefault && !removedDefault,
      };
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    // reagents are counted in tests: a kit (or bottle...) holding tests is required
    const noKit = needsTests
      ? form.variants.filter(({ breakdown }) => breakdown.length < 2)
      : [];
    if (noKit.length) {
      setError(
        `Add the kit level (how many tests it holds) to ${noKit
          .map(({ name }) => name || "the variant")
          .join(", ")}.`,
      );
      setOpenPackages(
        form.variants.reduce(
          (result, variant, i) => ({
            ...result,
            [i]: noKit.includes(variant) || result[i],
          }),
          openPackages,
        ),
      );
      return;
    }
    setError("");
    onSubmit({
      ...(selected?._id && { _id: selected._id }),
      pgid: Number(form.pgid),
      name: form.name.trim(),
      manufacturer: form.manufacturer.trim(),
      suppliers: form.suppliers,
      // null clears it on update
      supplier: form.supplier || null,
      tracksLot: !!form.tracksLot,
      storage: form.storage,
      description: form.description,
      isDefault: form.isDefault,
      // qnty (in base units) of every level is computed by the server
      variants: form.variants.map(
        ({ _id, name, breakdown, reorderLevel, reorderUnit, isDefault }) => ({
          ...(_id && { _id }),
          name: name.trim(),
          breakdown: breakdown.map(({ bu, su }) => ({
            bu,
            su: Number(su) || 1,
          })),
          reorderLevel:
            reorderLevel === "" ? undefined : Number(reorderLevel) || 0,
          // the largest unit when not chosen or no longer in the breakdown
          reorderUnit: breakdown.some(({ bu }) => bu === reorderUnit)
            ? reorderUnit
            : breakdown[0]?.bu || undefined,
          isDefault,
        }),
      ),
    });
  };

  return (
    <MDBModal isOpen={show} toggle={toggle} backdrop size="xl">
      <MDBModalHeader
        toggle={toggle}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="tag" className="mr-2" />
        {willCreate ? "Register" : "Update"} Branded Product
      </MDBModalHeader>
      <MDBModalBody>
        <form onSubmit={handleSubmit}>
          {/* generic tag */}
          <label className="small text-muted mb-1">
            <MDBIcon icon="cubes" className="mr-1" />
            Generic product *
          </label>
          <MDBRow>
            <MDBCol md="4">
              <input
                className="form-control mb-1"
                placeholder="Search generic..."
                value={genericKey}
                onChange={({ target }) => setGenericKey(target.value)}
              />
            </MDBCol>
            <MDBCol md="8">
              <select
                className="browser-default custom-select mb-1"
                value={form.pgid}
                onChange={({ target }) => handleChange("pgid", target.value)}
                required
              >
                <option value="">Select generic</option>
                {Object.entries(genericGroups).map(([section, generics]) => (
                  <optgroup key={section} label={section}>
                    {generics.map(({ id, name, abbreviation }) => (
                      <option key={id} value={id}>
                        {abbreviation} – {name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </MDBCol>
          </MDBRow>
          {generic && (
            <small className="d-block text-muted mb-3">
              {generic.category} · {generic.section} · uom: {generic.uom} ·{" "}
              {generic.reusable
                ? "Reusable (P.O. + monthly dispense)"
                : "Per test (declared upon dispensing)"}
            </small>
          )}

          <MDBRow className="mt-2">
            <MDBCol md="7">
              <label className="small text-muted mb-1">Brand name *</label>
              <input
                className="form-control mb-3"
                value={form.name}
                placeholder="e.g. BD Vacutainer K2EDTA, Terumo"
                onChange={({ target }) => handleChange("name", target.value)}
                required
              />
            </MDBCol>
            <MDBCol md="5">
              <label className="small text-muted mb-1">Manufacturer</label>
              <input
                className="form-control mb-3"
                value={form.manufacturer}
                placeholder="e.g. Becton Dickinson"
                onChange={({ target }) =>
                  handleChange("manufacturer", target.value)
                }
              />
            </MDBCol>
          </MDBRow>

          {/* supplier tags */}
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="small text-muted mb-0">
              <MDBIcon icon="truck" className="mr-1" />
              Suppliers{" "}
              <small>
                ({form.suppliers.length} tagged · <MDBIcon icon="star" /> =
                preferred when ordering)
              </small>
            </label>
            {suppliers.length > 6 && (
              <input
                className="form-control form-control-sm"
                style={{ maxWidth: 200 }}
                placeholder="Search supplier..."
                value={supplierKey}
                onChange={({ target }) => setSupplierKey(target.value)}
              />
            )}
          </div>
          <div
            className="border rounded p-2 mb-3 d-flex flex-wrap"
            style={{ gap: 6, maxHeight: 150, overflowY: "auto" }}
          >
            {!suppliers.length && (
              <small className="text-muted">
                No supplier yet. Add one in Sources &gt; Suppliers.
              </small>
            )}
            {shownSuppliers.map((supplier) => {
              const tagged = form.suppliers.includes(supplier._id);
              const preferred = form.supplier === supplier._id;
              return (
                <span
                  key={supplier._id}
                  className={`badge badge-pill d-inline-flex align-items-center py-2 px-3 ${
                    tagged ? "badge-primary" : "badge-light border"
                  }`}
                  style={{ cursor: "pointer", fontSize: "0.8rem" }}
                  onClick={() => toggleSupplier(supplier._id)}
                  title={tagged ? "Click to untag" : "Click to tag"}
                >
                  <MDBIcon icon={tagged ? "check" : "plus"} className="mr-1" />
                  {supplierLabel(supplier)}
                  {tagged && (
                    <MDBIcon
                      icon="star"
                      far={!preferred}
                      className={`ml-2 ${preferred ? "text-warning" : ""}`}
                      title="Set as preferred supplier"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreferred(supplier._id);
                      }}
                    />
                  )}
                </span>
              );
            })}
          </div>

          <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
            <label className="small text-muted mb-0">
              <MDBIcon icon="temperature-low" className="mr-1" />
              Storage
            </label>
            <select
              className="browser-default custom-select custom-select-sm"
              style={{ width: 200 }}
              value={form.storage}
              onChange={({ target }) => handleChange("storage", target.value)}
            >
              {Object.entries(STORAGE_TYPES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="custom-control custom-checkbox mb-3">
            <input
              type="checkbox"
              className="custom-control-input"
              id="product-tracksLot"
              checked={!!form.tracksLot}
              onChange={({ target }) =>
                handleChange("tracksLot", target.checked)
              }
            />
            <label className="custom-control-label" htmlFor="product-tracksLot">
              Has lot number and expiry
              <small className="text-muted d-block">
                Lot number and expiry date are required when receiving this
                brand.
              </small>
            </label>
          </div>

          {/* variants */}
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="small text-muted mb-0">
              Variants <small>(e.g. 3cc / 5cc, Tube 3mL)</small>
            </label>
            <MDBBtn
              size="sm"
              color="light"
              className="m-0"
              onClick={addVariant}
            >
              <MDBIcon icon="plus" className="mr-1" /> Variant
            </MDBBtn>
          </div>
          <MDBTable small bordered className="mb-2">
            <thead>
              <tr>
                <th
                  className="text-center"
                  style={{ width: 60 }}
                  title="Default variant of the generic, pre-selected when tagging and dispensing"
                >
                  Default
                </th>
                <th>Variant</th>
                <th>
                  Packaging{" "}
                  <small className="text-muted">(unit → contains → unit)</small>
                </th>
                <th
                  style={{ width: 170 }}
                  title="Low stock at or below this quantity"
                >
                  Reorder at
                </th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {form.variants.map((variant, index) => [
                <tr key={variant._id || index}>
                  <td className="text-center align-middle">
                    <MDBIcon
                      icon="star"
                      far={!(form.isDefault && variant.isDefault)}
                      className={
                        form.isDefault && variant.isDefault
                          ? "text-warning"
                          : "text-muted"
                      }
                      style={{ cursor: "pointer" }}
                      title={
                        form.isDefault && variant.isDefault
                          ? "Default of the generic (click to unset)"
                          : "Set as default of the generic"
                      }
                      onClick={() => toggleDefault(index)}
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={variant.name}
                      placeholder="3cc"
                      onChange={({ target }) =>
                        handleVariant(index, "name", target.value)
                      }
                      required
                    />
                  </td>
                  <td className="align-middle">
                    {(() => {
                      const levels = withQuantities(variant.breakdown);
                      const isOpen = !!openPackages[index];
                      const [top] = levels;
                      const baseUnit = levels[levels.length - 1]?.bu;
                      return (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 m-0 w-100 d-flex align-items-center text-left"
                          style={{ textTransform: "none" }}
                          title="Edit the packaging breakdown"
                          onClick={() =>
                            setOpenPackages((prev) => ({
                              ...prev,
                              [index]: !isOpen,
                            }))
                          }
                        >
                          <MDBIcon
                            icon="cube"
                            size="lg"
                            className="text-muted mr-2"
                          />
                          <span className="mr-auto">
                            {levels.length > 1 ? (
                              <>
                                <span className="badge blue lighten-5 text-primary mr-1">
                                  {top.bu}
                                </span>
                                · {Number(top.su) || "?"} {levels[1].bu}
                                <small className="d-block text-muted">
                                  {levels.length > 2 && `${chainOf(levels)} · `}
                                  1 {top.bu} = {top.qnty.toLocaleString()}{" "}
                                  {baseUnit} (base unit)
                                </small>
                              </>
                            ) : (
                              <>
                                <span className="badge blue lighten-5 text-primary mr-1">
                                  {top?.bu || "?"}
                                </span>
                                <small className="d-block text-muted">
                                  base unit only, add a level
                                </small>
                              </>
                            )}
                          </span>
                          <MDBIcon
                            icon={isOpen ? "chevron-up" : "chevron-down"}
                          />
                        </button>
                      );
                    })()}
                  </td>
                  <td className="align-middle">
                    {(() => {
                      // units of the breakdown, the largest by default
                      const units = variant.breakdown
                        .map(({ bu }) => bu)
                        .filter(Boolean);
                      const unit = units.includes(variant.reorderUnit)
                        ? variant.reorderUnit
                        : units[0] || "";
                      return (
                        <div className="input-group input-group-sm flex-nowrap">
                          <input
                            type="number"
                            min={0}
                            className="form-control"
                            style={{ minWidth: 60 }}
                            value={variant.reorderLevel}
                            placeholder="5"
                            title={`Low stock at or below this many ${
                              unit || "units"
                            }`}
                            onChange={({ target }) =>
                              handleVariant(index, "reorderLevel", target.value)
                            }
                          />
                          <div className="input-group-append">
                            <select
                              className="custom-select custom-select-sm"
                              style={{ borderRadius: "0 0.2rem 0.2rem 0" }}
                              value={unit}
                              disabled={!units.length}
                              onChange={({ target }) =>
                                handleVariant(
                                  index,
                                  "reorderUnit",
                                  target.value,
                                )
                              }
                            >
                              {units.map((bu) => (
                                <option key={bu} value={bu}>
                                  {bu}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="align-middle text-center">
                    {form.variants.length > 1 && (
                      <MDBIcon
                        icon="trash"
                        className="text-danger"
                        style={{ cursor: "pointer" }}
                        onClick={() => removeVariant(index)}
                        title="Remove variant"
                      />
                    )}
                  </td>
                </tr>,
                openPackages[index] && (
                  <tr key={`${variant._id || index}-packages`}>
                    <td colSpan={5} className="pt-0">
                      <PackagesEditor
                        levels={variant.breakdown}
                        base={baseOf(form.pgid)}
                        onChange={(breakdown) =>
                          handleVariant(index, "breakdown", breakdown)
                        }
                      />
                    </td>
                  </tr>
                ),
              ])}
            </tbody>
          </MDBTable>
          {error && (
            <div className="alert alert-danger py-1 px-2 small mb-2">
              <MDBIcon icon="exclamation-triangle" className="mr-1" />
              {error}
            </div>
          )}
          <small className="d-block text-muted mb-2">
            <MDBIcon icon="star" className="text-warning mr-1" />
            Default variant of {generic?.name || "the generic"}: pre-selected
            when tagging and dispensing. Only one per generic; when none is set,
            the first registered brand takes it.
          </small>
          {needsTests && (
            <small className="d-block text-muted mb-3">
              <MDBBadge color="info" className="mr-1">
                Reagent
              </MDBBadge>
              each test consumes 1 / (tests per unit) of the variant.
            </small>
          )}

          <label className="small text-muted mb-1">Description</label>
          <textarea
            className="form-control mb-3"
            rows={2}
            value={form.description}
            onChange={({ target }) => handleChange("description", target.value)}
          />

          <div className="text-center">
            <MDBBtn type="submit" color="info" rounded disabled={isLoading}>
              {willCreate ? "Submit" : "Update"}
            </MDBBtn>
          </div>
        </form>
      </MDBModalBody>
    </MDBModal>
  );
}
